import api from './api';
import type { Dimension, Unit } from '@/utils/units';

export interface SalesSummary {
  total_sales: number;
  total_revenue: number;
  average_ticket: number;
  total_items_sold: number;
}

export interface SalesByPeriod {
  period: string;
  total_sales: number;
  total_revenue: number;
}

export interface SalesByPaymentMethod {
  payment_method: string;
  total_sales: number;
  total_revenue: number;
  percentage: number;
}

export interface TopSellingProduct {
  product_id: number;
  product_name: string;
  category_name: string | null;
  quantity_sold: number;      // en base unit (unidades para count, gramos para weight)
  dimension: Dimension;
  display_unit: Unit;
  total_revenue: number;
}

export interface TopFractionedProduct {
  product_id: number;
  product_name: string;
  code: string | null;
  category_name: string | null;
  source_product_id: number | null;
  source_name: string | null;
  fraction_grams: number;     // gramos que descuenta cada unidad vendida
  units_sold: number;
  grams_sold: number;         // units_sold * fraction_grams
  total_revenue: number;
}

export interface DiscountByReason {
  reason_id: number;
  reason_name: string;
  sales_count: number;
  total_discounted: number;
  total_sold: number;
}

export interface SalesByCategory {
  category_id: number | null;
  category_name: string;
  total_sales: number;
  total_revenue: number;
  percentage: number;
}

export interface LowStockProduct {
  id: number;
  name: string;
  category_name: string | null;
  stock: number;              // en base unit
  minimum_stock: number;      // en base unit
  dimension: Dimension;
  display_unit: Unit;
  stock_ratio: number;
}

export interface DashboardStats {
  sales_summary: SalesSummary;
  sales_by_period: SalesByPeriod[];
  sales_by_payment_method: SalesByPaymentMethod[];
  top_selling_products: TopSellingProduct[];
  top_fractioned_products: TopFractionedProduct[];
  discounts_by_reason: DiscountByReason[];
  sales_by_category: SalesByCategory[];
  low_stock_products: LowStockProduct[];
}

interface DateRangeParams {
  start_date?: string;
  end_date?: string;
}

interface PeriodParams extends DateRangeParams {
  group_by?: 'day' | 'week' | 'month';
}

// API Functions
export const statsApi = {
  getDashboardStats: async (params?: DateRangeParams): Promise<DashboardStats> => {
    const response = await api.get('/stats/dashboard', { params });
    return response.data;
  },

  getSalesSummary: async (params?: DateRangeParams): Promise<SalesSummary> => {
    const response = await api.get('/stats/sales/summary', { params });
    return response.data;
  },

  getSalesByPeriod: async (params?: PeriodParams): Promise<SalesByPeriod[]> => {
    const response = await api.get('/stats/sales/by-period', { params });
    return response.data;
  },

  getSalesByPaymentMethod: async (params?: DateRangeParams): Promise<SalesByPaymentMethod[]> => {
    const response = await api.get('/stats/sales/by-payment-method', { params });
    return response.data;
  },

  getTopSellingProducts: async (params?: DateRangeParams & { limit?: number }): Promise<TopSellingProduct[]> => {
    const response = await api.get('/stats/products/top-selling', { params });
    return response.data;
  },

  getTopFractionedProducts: async (params?: DateRangeParams & { limit?: number }): Promise<TopFractionedProduct[]> => {
    const response = await api.get('/stats/products/top-fractioned', { params });
    return response.data;
  },

  getDiscountsByReason: async (params?: DateRangeParams): Promise<DiscountByReason[]> => {
    const response = await api.get('/stats/discounts/by-reason', { params });
    return response.data;
  },

  getSalesByCategory: async (params?: DateRangeParams): Promise<SalesByCategory[]> => {
    const response = await api.get('/stats/products/by-category', { params });
    return response.data;
  },

  getLowStockProducts: async (limit?: number): Promise<LowStockProduct[]> => {
    const response = await api.get('/stats/products/low-stock', { params: { limit } });
    return response.data;
  },
};
